import { useEffect, useState } from "react"

import { useMsal } from "@azure/msal-react"

import {
  getGatewayNetworkDiagram,
  getGatewayOverview,
  getPlantStatuses,
  type GatewayNetworkDiagram,
  type GatewayOverview,
  type NetworkConnection,
  type PlantAgent,
} from "@/api/getPlantStatus"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"

export default function PlantStatusBanner() {
  const { accounts } = useMsal()
  const [plants, setPlants] = useState<PlantAgent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const [networkDiagram, setNetworkDiagram] = useState<GatewayNetworkDiagram | null>(null)
  const [networkLoading, setNetworkLoading] = useState(false)
  const [networkError, setNetworkError] = useState<string | null>(null)
  const [expandedPlants, setExpandedPlants] = useState<Set<string>>(new Set())
  const [gatewayOverview, setGatewayOverview] = useState<GatewayOverview | null>(null)

  // Check if user has admin role to view sensitive information
  const userRoles = accounts[0]?.idTokenClaims?.roles || []
  const isAuthorized =
    userRoles.includes("SCADA.Diagnostics.Read") || userRoles.includes("Full.Dashboard.Admin")

  // Map machine names to friendly names
  const getFriendlyName = (machineName: string): string => {
    const nameMap: Record<string, string> = {
      "Ignition-PLTH-SRV2025": "Plumb Thicket",
      "Ignition-DHMGW0M3": "BC Organics",
      "lwrn-prod-ignition": "Lawrence",
    }
    return nameMap[machineName] || machineName
  }

  const fetchPlantStatuses = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await getPlantStatuses()
      setPlants(data)
      setLastUpdated(new Date())
    } catch (err) {
      console.error("Failed to fetch SCADA statuses:", err)
      setError(err instanceof Error ? err.message : "Failed to fetch SCADA statuses")
    } finally {
      setLoading(false)
    }
  }

  const fetchNetworkDiagram = async () => {
    try {
      setNetworkLoading(true)
      setNetworkError(null)
      const data = await getGatewayNetworkDiagram()
      setNetworkDiagram(data)
    } catch (err) {
      console.error("Failed to fetch network diagram:", err)
      setNetworkError(err instanceof Error ? err.message : "Failed to fetch network diagram")
    } finally {
      setNetworkLoading(false)
    }
  }

  const fetchGatewayOverview = async () => {
    try {
      const data = await getGatewayOverview()
      setGatewayOverview(data)
    } catch (err) {
      console.error("Failed to fetch gateway overview:", err)
      // Don't set error state for overview, just silently fail
    }
  }

  useEffect(() => {
    fetchPlantStatuses()

    // Only fetch detailed diagnostics if user is authorized
    if (isAuthorized) {
      fetchGatewayOverview()
      fetchNetworkDiagram() // Load network data immediately for plant hotlinks
    }

    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchPlantStatuses()
      if (isAuthorized) {
        fetchGatewayOverview()
      }
    }, 30000)
    return () => clearInterval(interval)
  }, [isAuthorized])

  const healthyCount = plants.filter((p) => p.isHealthy).length
  const totalCount = plants.length

  const getStatusClasses = (status: PlantAgent["status"]) => {
    switch (status) {
      case "Connected":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
      case "Trial Expired":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
      case "Disconnected":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
    }
  }

  const getStatusIcon = (status: PlantAgent["status"]) => {
    switch (status) {
      case "Connected":
        return "🟢"
      case "Trial Expired":
        return "🟡"
      case "Disconnected":
        return "🔴"
      default:
        return "⚪"
    }
  }

  const getConnectionStatusClasses = (status: string) => {
    switch (status.toLowerCase()) {
      case "running":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
      case "disconnected":
      case "error":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200"
    }
  }

  const formatRate = (rate: number | null | undefined): string => {
    if (rate == null) return "N/A"
    if (rate < 1000) return `${rate.toFixed(1)} B/s`
    if (rate < 1000000) return `${(rate / 1000).toFixed(1)} KB/s`
    return `${(rate / 1000000).toFixed(1)} MB/s`
  }

  const getConnectionsForPlant = (plantName: string): NetworkConnection[] => {
    if (!networkDiagram) return []
    return networkDiagram.connections.filter((conn) => conn.systemName === plantName)
  }
  const getGatewayUrl = (plantName: string): string | null => {
    const connections = getConnectionsForPlant(plantName)
    const connection = connections[0] // Take first connection
    if (!connection?.networkAddress) return null

    // Extract IP from "10.75.119.79:8060" format
    const ip = connection.networkAddress.split(":")[0]
    return `https://${ip}:443`
  }

  const openGateway = (plantName: string, event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    const url = getGatewayUrl(plantName)
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer")
    }
  }
  const togglePlantExpansion = (plantName: string) => {
    const newExpanded = new Set(expandedPlants)
    if (newExpanded.has(plantName)) {
      newExpanded.delete(plantName)
    } else {
      newExpanded.add(plantName)
      // Fetch network data if not already loaded
      if (!networkDiagram && !networkLoading) {
        fetchNetworkDiagram()
      }
    }
    setExpandedPlants(newExpanded)
  }

  if (loading && plants.length === 0) {
    return (
      <div className="flex items-center gap-2">
        <div className="h-2 w-2 rounded-full bg-gray-400 animate-pulse"></div>
        <span className="text-sm text-muted-foreground">Loading SCADA...</span>
      </div>
    )
  }

  if (error) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="sm" onClick={fetchPlantStatuses}>
              <span className="text-sm text-red-500">⚠️ CIS API Error</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{error}</p>
            <p className="text-xs text-muted-foreground">Click to retry</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return (
    <div className="flex items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
            aria-label="SCADA Status"
          >
            <div
              className={`h-2 w-2 rounded-full ${healthyCount === totalCount ? "bg-green-500" : healthyCount > 0 ? "bg-yellow-500" : "bg-red-500"}`}
            ></div>
            <span className="text-sm">
              SCADA {healthyCount}/{totalCount}
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-80 z-9999">
          <DropdownMenuLabel className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span>SCADA Status</span>
                {isAuthorized && (
                  <a
                    href="https://prod-cis-ignition.internal.sagepointenergy.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    title="Open CIS Gateway"
                  >
                    🔗 CIS
                  </a>
                )}
              </div>
              {isAuthorized && gatewayOverview && (
                <div className="text-xs font-normal text-muted-foreground">
                  Total: {formatRate(gatewayOverview.incomingByteRate)} ↓{" "}
                  {formatRate(gatewayOverview.outgoingByteRate)} ↑
                </div>
              )}
            </div>
            <Button variant="ghost" size="sm" onClick={fetchPlantStatuses} disabled={loading}>
              {loading ? "⟳" : "🔄"}
            </Button>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />

          {plants.length === 0 ? (
            <DropdownMenuItem disabled>No SCADA systems found</DropdownMenuItem>
          ) : (
            plants.map((plant) => {
              const connections = getConnectionsForPlant(plant.name)
              const isExpanded = expandedPlants.has(plant.name)
              return (
                <div key={plant.serverId} className="space-y-1">
                  <DropdownMenuItem className="flex-col items-start space-y-1">
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-2">
                        {isAuthorized && getGatewayUrl(plant.name) ? (
                          <button
                            onClick={(e) => openGateway(plant.name, e)}
                            className="font-medium text-sm text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
                            title={`Open ${getFriendlyName(plant.name)} Gateway`}
                          >
                            {getFriendlyName(plant.name)} 🔗
                          </button>
                        ) : (
                          <span className="font-medium text-sm">{getFriendlyName(plant.name)}</span>
                        )}
                      </div>
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${getStatusClasses(plant.status)}`}
                      >
                        {getStatusIcon(plant.status)} {plant.status}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground w-full">
                      {isAuthorized && <div>Memory: {plant.usedMemoryMB} MB</div>}
                      <div>
                        Last:{" "}
                        {new Date(
                          plant.lastCommDateTime +
                            (plant.lastCommDateTime.endsWith("Z") ? "" : "Z"),
                        ).toLocaleTimeString()}
                      </div>
                      {isAuthorized && plant.lastEvent && (
                        <div className="truncate" title={plant.lastEvent}>
                          Event: {plant.lastEvent}
                        </div>
                      )}
                    </div>
                  </DropdownMenuItem>

                  {/* Network connections for this specific plant - only show for authorized users */}
                  {isAuthorized && (
                    <>
                      <DropdownMenuItem
                        className="flex items-center justify-between cursor-pointer ml-4 text-xs text-muted-foreground hover:bg-accent"
                        onClick={(e) => {
                          e.preventDefault()
                          togglePlantExpansion(plant.name)
                        }}
                      >
                        <span>Network Status ({connections.length})</span>
                        <span>{isExpanded ? "▼" : "▶"}</span>
                      </DropdownMenuItem>

                      {isExpanded && (
                        <div className="space-y-1">
                          {networkLoading && (
                            <DropdownMenuItem
                              disabled
                              className="text-xs text-muted-foreground ml-6"
                            >
                              Loading network data...
                            </DropdownMenuItem>
                          )}

                          {networkError && (
                            <DropdownMenuItem disabled className="text-xs text-red-500 ml-6">
                              {networkError}
                            </DropdownMenuItem>
                          )}

                          {connections.length === 0 && networkDiagram && (
                            <DropdownMenuItem
                              disabled
                              className="text-xs text-muted-foreground ml-6"
                            >
                              No network connections found
                            </DropdownMenuItem>
                          )}

                          {connections.map((connection, index) => (
                            <DropdownMenuItem
                              key={connection.connectionId || index}
                              disabled
                              className="flex-col items-start space-y-1 ml-6 border-l-2 border-gray-300 pl-2"
                            >
                              <div className="flex items-center justify-between w-full">
                                <span className="font-medium text-xs">{connection.direction}</span>
                                <span
                                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${getConnectionStatusClasses(connection.connectionStatus)}`}
                                >
                                  {connection.connectionStatus}
                                </span>
                              </div>

                              <div className="text-xs text-muted-foreground w-full space-y-1">
                                <div className="flex justify-between">
                                  <span>Ping: {connection.pingTime}ms</span>
                                  <span>
                                    v{connection.version.split(".").slice(0, 2).join(".")}
                                  </span>
                                </div>

                                {isAuthorized && (
                                  <>
                                    {connection.networkAddress && (
                                      <div>Address: {connection.networkAddress}</div>
                                    )}
                                    <div className="flex justify-between">
                                      <span>SSL: {connection.ssl ? "✓" : "✗"}</span>
                                      <span>Faults: {connection.faultCount || 0}</span>
                                    </div>
                                    <div className="flex justify-between text-xs">
                                      <span>↑ {formatRate(connection.outgoing_rate)}</span>
                                      <span>↓ {formatRate(connection.incoming_rate)}</span>
                                    </div>
                                  </>
                                )}
                              </div>
                            </DropdownMenuItem>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )
            })
          )}

          <DropdownMenuSeparator />
          <DropdownMenuItem disabled className="text-xs text-muted-foreground">
            {lastUpdated ? `Updated: ${lastUpdated.toLocaleTimeString()}` : "Never updated"}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
